import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateGrantInput,
  ListGrantsFilters,
  PortalSubjectType,
  UpdateGrantInput,
  portalAccessService,
} from "../../services/admin/portalAccess.service";

const ROOT_KEY = ["admin", "portal-access"] as const;

export function usePortalAccessGrants(filters: ListGrantsFilters = {}) {
  return useQuery({
    queryKey: [...ROOT_KEY, "list", filters],
    queryFn: () => portalAccessService.list(filters),
  });
}

export function usePortalAccessGrantsForSubject(
  subjectType: PortalSubjectType | null,
  subjectId: string | null | undefined
) {
  return useQuery({
    queryKey: [...ROOT_KEY, "subject", subjectType, subjectId],
    queryFn: () => portalAccessService.listForSubject(subjectType!, subjectId!),
    enabled: Boolean(subjectType && subjectId),
  });
}

export function useCreatePortalAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGrantInput) => portalAccessService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

export function useUpdatePortalAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      grantId,
      input,
    }: {
      grantId: string;
      input: UpdateGrantInput;
    }) => portalAccessService.update(grantId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

export function useRevokePortalAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => portalAccessService.revoke(grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

export function useRestorePortalAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => portalAccessService.restore(grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

/**
 * Returns a Set of subject IDs that currently have at least one eligible
 * portal access grant (invited or active) for the given subject type.
 * Also returns a Set of subject IDs that have at least one fully active grant.
 * Useful for table rows that need to show a portal-access indicator per
 * subject without fanning out N queries.
 */
export function usePortalAccessSubjectIdSet(subjectType: PortalSubjectType) {
  const query = usePortalAccessGrants({
    subjectType,
    state: ["active", "invited"],
  });

  const subjectIdSet = useMemo(
    () => new Set((query.data ?? []).map((g) => g.subjectId)),
    [query.data]
  );

  const activeIdSet = useMemo(
    () =>
      new Set(
        (query.data ?? [])
          .filter((g) => g.state === "active")
          .map((g) => g.subjectId)
      ),
    [query.data]
  );

  return {
    subjectIdSet, // Subjects with invited OR active grants (eligible for delivery setup)
    activeIdSet, // Subjects with at least one active grant
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useResendPortalAccessInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) =>
      portalAccessService.resendInvitation(grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}
